import React, { forwardRef, useCallback, useMemo, useRef, useState, useEffect, useImperativeHandle } from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import BaseHierarchy, {
  BaseHierarchyHandle,
  HierarchyItem,
  HierarchyPosition,
  HierarchyLink
} from './BaseHierarchy';
import DraggableHierarchyItem, { DraggableHierarchyItemHandle } from './DraggableHierarchyItem';
import ModuleCard from './ModuleCard';
import ArrowView from '@navigation/screens/MidLevel/UsersScreen/utils/ArrowView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import ModuleLinkHandler, { ModuleLinkHandlerHandle } from './ModuleLinkHandler';
import Svg, { Line, Polygon } from 'react-native-svg';
import DraggableModuleCard from './DraggableModuleCard';

// Helper function to calculate connection points
const getConnectionPoint = (
  position: HierarchyPosition,
  side: 'left' | 'right' | 'top' | 'bottom',
  componentWidth: number = 280,
  componentHeight: number = 160
) => {
  switch (side) {
    case 'left':
      return { x: position.x, y: position.y + componentHeight / 2 };
    case 'right':
      return { x: position.x + componentWidth, y: position.y + componentHeight / 2 };
    case 'top':
      return { x: position.x + componentWidth / 2, y: position.y };
    case 'bottom':
      return { x: position.x + componentWidth / 2, y: position.y + componentHeight };
    default:
      return { x: position.x + componentWidth / 2, y: position.y + componentHeight / 2 };
  }
};


export interface ModuleItem extends HierarchyItem {
  _id: string;
  title: string;
  description?: string;
  type: 'video' | 'text' | 'quiz' | 'assignment' | 'primary';
  content?: string;
  duration?: string;
  level?: string;
  isCompleted?: boolean;
  connections?: { targetId: string; type: string }[]; // For prerequisites
  [key: string]: any;
}

interface AcademyHierarchyProps {
  courseId: string;
  courseTitle: string;
  courseDescription?: string;
  initialModules: ModuleItem[];
  mode: 'edit' | 'learn';
  onModulePositionChange?: (moduleId: string, position: HierarchyPosition) => void;
  onModuleConnectionChange?: (sourceId: string, targetId: string, type: string) => void;
  onModuleEdit?: (module: ModuleItem) => void;
  onModuleDelete?: (moduleId: string) => void;
}

export interface AcademyHierarchyHandle extends BaseHierarchyHandle {
  addModule: (module: ModuleItem) => void;
  removeModule: (moduleId: string) => void;
  updateModule: (moduleId: string, updates: Partial<ModuleItem>) => void;
  getModulesWithPositionsAndConnections: () => ModuleItem[];
}

const AcademyHierarchy = forwardRef<AcademyHierarchyHandle, AcademyHierarchyProps>((props, ref) => {
  const {
    courseId,
    courseTitle,
    courseDescription,
    initialModules,
    mode,
    onModulePositionChange,
    onModuleConnectionChange,
    onModuleEdit,
    onModuleDelete
  } = props;

  const theme = themeSelector();

  const baseHierarchyRef = useRef<BaseHierarchyHandle>(null);
  const draggableModuleRefs = useRef<Map<string, DraggableModuleCardHandle>>(new Map());
  const modulePositionsRef = useRef<Record<string, HierarchyPosition>>({});
  const moduleLinkHandlerRef = useRef<ModuleLinkHandlerHandle>(null);

  const [modules, setModules] = useState<ModuleItem[]>(initialModules);
  const [modulePositions, setModulePositions] = useState<Record<string, HierarchyPosition>>({});
  const [connections, setConnections] = useState<Array<{ sourceId: string, targetId: string, type: string }>>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState<{ moduleId: string; side: 'top' | 'right' | 'bottom' | 'left' } | null>(null);
  const [dragArrow, setDragArrow] = useState<{ from: HierarchyPosition; to: HierarchyPosition } | null>(null);

  // Update modules when initialModules prop changes (for content updates)
  useEffect(() => {
    setModules(initialModules);
  }, [initialModules]);

  // Initialize modulePositionsRef and modulePositions state with initial positions
  useEffect(() => {
    const initialPositions: Record<string, HierarchyPosition> = {};

    initialModules.forEach(module => {
      if (module.x !== undefined && module.y !== undefined) {
        const position = { x: module.x, y: module.y };
        modulePositionsRef.current[module._id] = position;
        initialPositions[module._id] = position;
      }
    });

    // Also initialize the primary course position (centered in viewport)
    if (courseId) {
      const componentWidth = 280;
      const componentHeight = 160;
      const primaryPosition = {
        x: (Platform.OS === 'android' ? 1500 : 2000) - (componentWidth / 2), // Center horizontally
        y: (Platform.OS === 'android' ? 1500 : 2000) - (componentHeight / 2)  // Center vertically
      };
      modulePositionsRef.current[courseId] = primaryPosition;
      initialPositions[courseId] = primaryPosition;
    }

    setModulePositions(initialPositions);
  }, [initialModules, courseId]);

  // Create primary course item (similar to primaryAction in ActionHierarchy)
  const primaryCourseItem: ModuleItem | undefined = useMemo(() => {
    return {
      _id: courseId || 'primary-course',
      title: courseTitle || 'Main Course',
      description: courseDescription || 'This is the main course overview and learning objectives.',
      type: 'primary',
      duration: 'Course Overview',
      level: 'All Levels',
      isCompleted: false,
      // Position primary course centered in the viewport - Android-safe sizing
      x: (Platform.OS === 'android' ? 1500 : 2000) - 140, // Center horizontally
      y: (Platform.OS === 'android' ? 1500 : 2000) - 80,  // Center vertically
    };
  }, [courseId, courseTitle, courseDescription]);

  // Convert modules to ModuleItem format with smart positioning around primary course
  const moduleItems: ModuleItem[] = useMemo(() => {
    if (!modules || modules.length === 0) return [];

    // Converting modules to moduleItems

    return modules.map((module, index) => {
      // Calculate hierarchical position for modules
      let calculatedPosition = { x: 0, y: 0 };

      if (primaryCourseItem) {
        // Create hierarchical layout: modules to the right of primary course
        const primaryX = primaryCourseItem.x || 600;
        const primaryY = primaryCourseItem.y || (Platform.OS === 'android' ? 1500 : 2000);

        // Organize modules in levels to the right with proper component dimensions
        const componentWidth = 280;
        const componentHeight = 160;
        const modulesPerLevel = 4; // Maximum modules per level
        const levelSpacing = componentWidth + 100; // Horizontal spacing between levels (card width + gap)
        const moduleSpacing = componentHeight + 50; // Vertical spacing between modules in same level (card height + gap)

        const level = Math.floor(index / modulesPerLevel);
        const positionInLevel = index % modulesPerLevel;

        // Calculate position relative to primary course
        const levelX = primaryX + levelSpacing + (level * levelSpacing);
        const levelY = primaryY + (positionInLevel - (modulesPerLevel - 1) / 2) * moduleSpacing;

        calculatedPosition = {
          x: levelX,
          y: levelY
        };
      } else {
        // If no primary course, position in a grid
        const cols = Math.ceil(Math.sqrt(modules.length));
        const row = Math.floor(index / cols);
        const col = index % cols;
        calculatedPosition = {
          x: col * 350,
          y: row * 450
        };
      }

      return {
        ...module,
        // Preserve existing position if available, otherwise use calculated position
        x: module.x !== undefined ? module.x : calculatedPosition.x,
        y: module.y !== undefined ? module.y : calculatedPosition.y,
      };
    });
  }, [modules, primaryCourseItem]);

  // Sync positions from BaseHierarchy to modulePositionsRef for link rendering (like ActionHierarchy)
  useEffect(() => {
    const syncPositions = () => {
      const allPositions = baseHierarchyRef.current?.getAllItemPositions() || {};
      console.log('🔄 Syncing module positions for links:', {
        allPositionsCount: Object.keys(allPositions).length,
        allPositions: allPositions,
        currentModulePositionsCount: Object.keys(modulePositionsRef.current).length
      });

      // Update modulePositionsRef with all positions from BaseHierarchy
      modulePositionsRef.current = { ...allPositions };

      // Force link handler update
      if (moduleLinkHandlerRef.current?.forceUpdate) {
        moduleLinkHandlerRef.current.forceUpdate();
      }
    };

    // Sync positions after a short delay to ensure BaseHierarchy has initialized
    const timeoutId = setTimeout(syncPositions, 100);

    return () => clearTimeout(timeoutId);
  }, [moduleItems, primaryCourseItem]); // Re-sync when items change

  // Additional sync after BaseHierarchy has had time to initialize positions
  useEffect(() => {
    const syncAfterMount = () => {
      const allPositions = baseHierarchyRef.current?.getAllItemPositions() || {};
      console.log('🔄 Post-mount module position sync:', {
        allPositionsCount: Object.keys(allPositions).length,
        allPositions: allPositions
      });

      // Update modulePositionsRef with all positions from BaseHierarchy
      modulePositionsRef.current = { ...allPositions };

      // Force link handler update
      if (moduleLinkHandlerRef.current?.forceUpdate) {
        moduleLinkHandlerRef.current.forceUpdate();
      }
    };

    // Sync after BaseHierarchy has mounted and calculated positions
    const timeoutId = setTimeout(syncAfterMount, 500);

    return () => clearTimeout(timeoutId);
  }, []); // Run once after mount

  // Handle adding sub-modules
  const handleAddSubModule = useCallback((parentModuleId: string) => {
    const newSubModule: ModuleItem = {
      _id: `submodule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: 'New Sub-Module',
      description: 'Click to edit this sub-module',
      type: 'video',
      content: '',
      duration: '0 min',
      level: 'Beginner',
      isCompleted: false,
      resources: [],
      order: modules?.length || 0,
      x: 0, // Will be positioned relative to parent
      y: 0, // Will be positioned relative to parent
    };

    // Find parent module position
    const parentPosition = modulePositions[parentModuleId] || { x: 0, y: 0 };

    // Position sub-module to the right of parent with some offset
    const subModulePosition = {
      x: parentPosition.x + 320, // 320px to the right (280px card width + 40px gap)
      y: parentPosition.y + 40,  // 40px down for slight offset
    };

    // Add the new sub-module
    setModules(prev => [...prev, { ...newSubModule, x: subModulePosition.x, y: subModulePosition.y }]);

    // Update positions
    setModulePositions(prev => ({
      ...prev,
      [newSubModule._id]: subModulePosition
    }));

    modulePositionsRef.current[newSubModule._id] = subModulePosition;

    // Create connection from parent to sub-module
    setConnections(prev => [...prev, {
      sourceId: parentModuleId,
      targetId: newSubModule._id,
      type: 'has_submodule'
    }]);

    console.log('➕ Added sub-module:', {
      parentModuleId,
      subModuleId: newSubModule._id,
      position: subModulePosition
    });
  }, [modules, modulePositions]);


  // Debug final moduleItems
  useEffect(() => {
    // Final moduleItems calculated
  }, [moduleItems]);

  // Combine all items for BaseHierarchy (primary course + modules)
  const allHierarchyItems: ModuleItem[] = useMemo(() => {
    const items: ModuleItem[] = [];

    // Add primary course item if it exists
    if (primaryCourseItem) {
      items.push(primaryCourseItem);
    }

    // Add all module items
    items.push(...moduleItems);

    // Combined items for BaseHierarchy

    return items;
  }, [primaryCourseItem, moduleItems]);

  // Initialize modules and connections from initialModules
  useEffect(() => {
    const loadedModules: ModuleItem[] = [];
    const loadedConnections: { sourceId: string; targetId: string; type: string; }[] = [];

    initialModules.forEach(mod => {
      loadedModules.push({
        ...mod,
        x: mod.x || 0, // Ensure x, y are numbers
        y: mod.y || 0,
      });
      if (mod.connections) {
        mod.connections.forEach(conn => {
          loadedConnections.push({ sourceId: mod._id, targetId: conn.targetId, type: conn.type });
        });
      }
    });
    setModules(loadedModules);
    setConnections(prev => [...prev, ...loadedConnections]);
  }, []); // Only run once on mount

  // Generate links between modules (similar to actionLinks in ActionHierarchy)
  const moduleLinks: HierarchyLink[] = useMemo(() => {
    const links: HierarchyLink[] = [];


    // Add user-created connections
    connections.forEach(connection => {
      const sourcePos = modulePositions[connection.sourceId];
      const targetPos = modulePositions[connection.targetId];

      if (sourcePos && targetPos) {
        links.push({
          sourceId: connection.sourceId,
          targetId: connection.targetId,
          type: connection.type,
          width: connection.type === 'prerequisite' ? 2 : 3
        });
      }
    });

    // Add automatic links for hierarchical learning path (always add primary course connections)
    if (primaryCourseItem && moduleItems.length > 0) {
      const modulesPerLevel = 4; // Same as in positioning logic

      // Connect primary course to ALL modules (only if not already connected and not sub-modules)
      moduleItems.forEach(module => {
        // Skip sub-modules - they should only connect to their parent
        const isSubModule = module._id.startsWith('submodule_');
        if (isSubModule) return;

        // Check if this module is already connected to the primary course in existing connections
        const isAlreadyConnectedToPrimary = connections.some(
          conn => conn.sourceId === primaryCourseItem._id && conn.targetId === module._id
        );

        // If not connected, and it's not the primary course itself, add a link
        if (!isAlreadyConnectedToPrimary && module._id !== primaryCourseItem._id) {
          links.push({
            sourceId: primaryCourseItem._id,
            targetId: module._id,
            type: 'starts_with',
            width: 3,
            sourceConnectionPoint: 'right',
            targetConnectionPoint: 'left'
          });
        }
      });

      // REMOVED: Horizontal connections between modules at same level
      // REMOVED: Vertical connections between levels
      // Only primary course connects to modules - clean parent-child relationships
    }


    return links;
  }, [connections, modulePositions, primaryCourseItem, moduleItems]);

  // Handle module position changes (only on drop, updates state for persistence)
  const handleModulePositionChange = useCallback((moduleId: string, position: HierarchyPosition) => {
    // Update ref first (always current)
    modulePositionsRef.current[moduleId] = position;

    // Update state for persistence (causes re-render, but only on drop)
    setModulePositions(prev => ({
      ...prev,
      [moduleId]: position
    }));

    // Trigger ModuleLinkHandler update efficiently using imperative handle
    if (moduleLinkHandlerRef.current?.forceUpdate) {
      moduleLinkHandlerRef.current.forceUpdate();
    }

    onModulePositionChange?.(moduleId, position);
  }, [onModulePositionChange]);

  // Handle continuous position updates during drag (only updates refs, no state changes)
  const handleModulePositionUpdate = useCallback((moduleId: string, position: HierarchyPosition) => {
    // Update ref for real-time tracking
    const oldPosition = modulePositionsRef.current[moduleId];
    modulePositionsRef.current[moduleId] = position;

    // Log ref updates to verify they're happening
    console.log(`🔄 REF UPDATE: ${moduleId}`, {
      old: oldPosition,
      new: position,
      changed: oldPosition ? (oldPosition.x !== position.x || oldPosition.y !== position.y) : true
    });

    // No state updates - links read from refs directly
  }, []);

  // Helper function to get connection point position
  const getConnectionPointPosition = useCallback((moduleId: string, side: 'top' | 'right' | 'bottom' | 'left') => {
    const position = modulePositions[moduleId];
    if (!position) return { x: 0, y: 0 };

    const cardWidth = 200;
    const cardHeight = 120;

    switch (side) {
      case 'top':
        return { x: position.x + cardWidth / 2, y: position.y };
      case 'right':
        return { x: position.x + cardWidth, y: position.y + cardHeight / 2 };
      case 'bottom':
        return { x: position.x + cardWidth / 2, y: position.y + cardHeight };
      case 'left':
        return { x: position.x, y: position.y + cardHeight / 2 };
      default:
        return position;
    }
  }, [modulePositions]);

  // Handle 4-side connection creation
  const handleConnectionStart = useCallback((moduleId: string, side: 'top' | 'right' | 'bottom' | 'left') => {
    if (mode !== 'edit') return;
    console.log(`🔗 Connection started: ${moduleId} (${side})`);
    setIsConnecting(true);
    setConnectionStart({ moduleId, side });

    // Initialize drag arrow
    const fromPosition = getConnectionPointPosition(moduleId, side);
    setDragArrow({ from: fromPosition, to: fromPosition });
  }, [mode, getConnectionPointPosition]);

  const handleConnectionEnd = useCallback((targetModuleId: string, targetSide: 'top' | 'right' | 'bottom' | 'left') => {
    if (mode !== 'edit') return;
    if (connectionStart && connectionStart.moduleId !== targetModuleId) {
      console.log(`🔗 Connection created: ${connectionStart.moduleId} -> ${targetModuleId}`);
      setConnections(prev => [...prev, {
        sourceId: connectionStart.moduleId,
        sourceSide: connectionStart.side,
        targetId: targetModuleId,
        targetSide: targetSide,
        type: 'prerequisite'
      }]);
      onModuleConnectionChange?.(connectionStart.moduleId, targetModuleId, 'prerequisite');
    }
    setIsConnecting(false);
    setConnectionStart(null);
    setDragArrow(null);
  }, [mode, connectionStart, onModuleConnectionChange]);

  const handleConnectionCancel = useCallback(() => {
    if (mode !== 'edit') return;
    console.log('❌ Connection cancelled');
    setIsConnecting(false);
    setConnectionStart(null);
    setDragArrow(null);
  }, [mode]);

  // Function to update drag arrow position
  const updateDragArrow = useCallback((toPosition: HierarchyPosition) => {
    if (connectionStart) {
      const fromPosition = getConnectionPointPosition(connectionStart.moduleId, connectionStart.side);
      setDragArrow({ from: fromPosition, to: toPosition });
    }
  }, [connectionStart, getConnectionPointPosition]);

  // Render function for module items
  const renderModuleItem = useCallback((
    module: ModuleItem,
    position: HierarchyPosition,
    onPositionChange: (position: HierarchyPosition) => void,
    zoomLevel?: number
  ) => {
    return (
      <DraggableModuleCard
        ref={(ref) => {
          if (ref) {
            draggableModuleRefs.current.set(module._id, ref);
          } else {
            draggableModuleRefs.current.delete(module._id);
          }
        }}
        module={{
          ...module,
          order: module.order || 0,
          description: module.description || '',
          content: module.content || '',
          duration: typeof module.duration === 'number' ? module.duration : 0,
          resources: module.resources || [],
          type: module.type === 'primary' ? 'video' : module.type // Convert primary to video for CourseModule compatibility
        }}
        initialPosition={position}
        onPositionChange={onPositionChange}
        onPositionUpdate={(position: HierarchyPosition) => {
          handleModulePositionUpdate(module._id, position);
        }} // New prop for continuous updates
        dragEnabled={mode === 'edit'}
        isEditMode={mode === 'edit'}
        isPrimary={module.type === 'primary'}
        onEditModule={(moduleId: string) => {
          const module = modules.find(m => m._id === moduleId);
          if (module && onModuleEdit) {
            onModuleEdit(module);
          }
        }}
        onDeleteModule={(moduleId: string) => onModuleDelete?.(moduleId)}
        onAddSubModule={(parentModuleId: string) => {
          console.log('🎯 onAddSubModule called:', { parentModuleId, handleAddSubModule: !!handleAddSubModule });
          if (handleAddSubModule) {
            handleAddSubModule(parentModuleId);
          } else {
            console.error('❌ handleAddSubModule is not defined');
          }
        }}
        onSnap={(targetId, sourceId) => {
          // Module snapped
          // Handle connection creation here
          setConnections(prev => [...prev, {
            sourceId,
            targetId,
            type: 'learning_path',
            sourceConnectionPoint: 'right',
            targetConnectionPoint: 'left'
          }]);
        }}
        arrowHandler={({ id, path, reset }) => {
          // Arrow handler update
          // Handle arrow updates through ModuleLinkHandler
          if (moduleLinkHandlerRef.current) {
            moduleLinkHandlerRef.current.updateArrow({ id, path, reset });
          }
        }}
        zoomProps={{
          zoomLevel: zoomLevel || 1,
          offsetX: 0,
          offsetY: 0,
        }}
        getAllModulePositions={() => modulePositions}
        zoomLevel={zoomLevel}
        linkStartPosition="right" // Default to right side for all modules
        linkEndPosition="left" // Default to left side for incoming links
        onComponentMeasured={(measurements) => {
          console.log(`📏 Component measured: ${module._id}`, measurements);
        }}
      />
    );
  }, [mode, onModuleEdit, onModuleDelete, modulePositions]);

  // Module management functions
  const addModule = useCallback((newModule: ModuleItem) => {
    console.log('➕ Adding module:', newModule.title);
    setModules(prev => {
      const updatedModules = [...prev, newModule];
      // Recalculate positions for all modules to maintain layout
      const newPositions = calculateModulePositions(updatedModules);
      return updatedModules.map(mod => ({
        ...mod,
        x: newPositions[mod._id]?.x || mod.x,
        y: newPositions[mod._id]?.y || mod.y,
      }));
    });
  }, []);

  const removeModule = useCallback((moduleId: string) => {
    console.log('➖ Removing module:', moduleId);
    setModules(prev => prev.filter(mod => mod._id !== moduleId));
    setConnections(prev => prev.filter(conn => conn.sourceId !== moduleId && conn.targetId !== moduleId));
  }, []);

  const updateModule = useCallback((moduleId: string, updates: Partial<ModuleItem>) => {
    console.log('✏️ Updating module:', moduleId);
    setModules(prev => prev.map(mod => mod._id === moduleId ? { ...mod, ...updates } : mod));
  }, []);

  // Calculate positions for new modules or if strategy changes
  const calculateModulePositions = useCallback((itemsArray: ModuleItem[]) => {
    const positions: Record<string, HierarchyPosition> = {};
    const startX = 100;
    const startY = 100;
    const horizontalSpacing = 400;
    const verticalSpacing = 250;
    const zigzagOffset = 100; // Offset for zigzag pattern

    itemsArray.forEach((item, i) => {
      // Simple linear layout for now, can be expanded to more complex
      const row = Math.floor(i / 2); // Two items per "row" for zigzag
      const col = i % 2;

      let x = startX + col * horizontalSpacing;
      let y = startY + row * verticalSpacing;

      if (col === 1) { // Apply zigzag offset to alternate columns
        y += zigzagOffset;
      }

      positions[item._id] = { x, y };
    });
    return positions;
  }, []);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    ...baseHierarchyRef.current!,
    addModule,
    removeModule,
    updateModule,
    getModulesWithPositionsAndConnections: () => {
      return modules.map(mod => ({
        ...mod,
        position: modulePositions[mod._id] || { x: mod.x, y: mod.y },
        connections: connections.filter(conn => conn.sourceId === mod._id).map(conn => ({
          targetId: conn.targetId,
          type: conn.type
        }))
      }));
    }
  }));

  // Render visual connections
  const renderConnections = () => {
    return connections.map((connection, index) => {
      const sourcePos = modulePositions[connection.sourceId];
      const targetPos = modulePositions[connection.targetId];

      if (!sourcePos || !targetPos) return null;

      return (
        <ArrowView
          key={`connection-${index}`}
          from={{
            x: sourcePos.x + 150, // Center of module card (assuming 300px width)
            y: sourcePos.y + 90   // Bottom of module card (assuming 180px height)
          }}
          to={{
            x: targetPos.x + 150, // Center of target module card
            y: targetPos.y        // Top of target module card
          }}
          startColor="#1E40AF"
          endColor="#3B82F6"
          width={3}
          gap={20}
          curveDelta={50}
          data={connection}
          showAvs={false}
          text=""
          pressCallback={() => {
            // Connection clicked
            // Could open connection properties dialog
          }}
        />
      );
    });
  };



  // AcademyHierarchy rendering

  return (
    <View
      style={{
        flex: 1,
        position: 'relative',
        ...(Platform.OS !== 'web' && { pointerEvents: 'auto' } as any)
      }}
      pointerEvents={Platform.OS !== 'web' ? 'auto' : undefined}
    >
      <BaseHierarchy
        ref={baseHierarchyRef}
        items={allHierarchyItems}
        primaryItem={primaryCourseItem}
        links={moduleLinks}
        isLoading={false}
        onItemPositionChange={handleModulePositionChange}
        renderItem={renderModuleItem}
        renderLinkHandler={({ itemPositionsRef, links, theme, zoomLevel }) => (
          <ModuleLinkHandler
            ref={moduleLinkHandlerRef}
            modulePositionsRef={modulePositionsRef}
            links={links}
            theme={theme}
            zoomLevel={zoomLevel}
          />
        )}
        // Configuration for hierarchical layout - Reduced size for Android to prevent crashes
        viewportSize={Platform.OS === 'android'
          ? { width: 3000, height: 3000 }
          : { width: 4000, height: 4000 }}
        gridSize={50}
        initialZoom={0.6} // Slightly closer to see the hierarchical structure better
        minZoom={0.2}
        maxZoom={3}
        positioningStrategy="manual" // Use manual positioning to preserve existing positions
        centerSpacing={600} // Match the level spacing
        showGrid={true}
        showDebugInfo={__DEV__}
        backgroundColor={undefined} // Use theme default
      />

      {/* Connection Mode Instructions */}
      {isConnecting && mode === 'edit' && (
        <View style={{
          position: 'absolute',
          top: 20,
          left: 20,
          right: 20,
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.8)',
          padding: 16,
          borderRadius: 12,
          zIndex: 1000,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 8
        }}>
          <Text style={{
            fontSize: 16,
            fontWeight: 'bold',
            color: theme ? '#374151' : '#f9fafb',
            textAlign: 'center',
            marginBottom: 8
          }}>
            🔗 Creating Module Connection
          </Text>
          <Text style={{
            fontSize: 14,
            color: theme ? '#6b7280' : '#d1d5db',
            textAlign: 'center'
          }}>
            Click on a connection point to complete the connection, or press ESC to cancel.
          </Text>
        </View>
      )}

    </View>
  );
});

AcademyHierarchy.displayName = 'AcademyHierarchy';

export default AcademyHierarchy;